import type { ProjectUser } from "@/types/api";

/**
 * The subset of a user needed to render their identity. Widened from
 * {@link ProjectUser} so callers holding a richer user object (a profile with
 * an avatar, a record creator) can pass it straight through.
 */
export type DisplayablePerson = Pick<
  ProjectUser,
  "first_name" | "last_name" | "email"
>;

/**
 * Display name for a person, or `null` when we hold no identity for them.
 *
 * Falls back to the email because names are **optional** on the backend user
 * record — only `id` and `email` are required. An account that arrives through
 * an SSO provider carrying a profile has both name fields filled; one created
 * by email invite typically has neither, and rendering it by name alone
 * produces a nameless line. The email is the only field guaranteed to be
 * there, and it is already expanded onto every payload that carries a user, so
 * this needs no extra request.
 *
 * `null` is a distinct third case, not a formatting failure: the share view and
 * not-yet-synced offline drafts synthesize a user with *every* field empty
 * because those payloads carry no uploader identity at all. Callers decide how
 * to word that — this refuses to invent a name for it.
 */
export function personDisplayName(
  person: DisplayablePerson | null | undefined,
): string | null {
  if (!person) return null;
  const name = [person.first_name, person.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || person.email?.trim() || null;
}
